import { useState, useEffect, useRef } from 'react';

/**
 * LazyLoad - Viewport-based lazy loading wrapper
 * 
 * Only renders children when the component enters the viewport.
 * Uses IntersectionObserver for efficient viewport detection.
 * 
 * @param {ReactNode} children - The content to lazy load
 * @param {ReactNode} fallback - Skeleton/placeholder to show while waiting
 * @param {string} rootMargin - Distance before viewport to start loading (default: 200px)
 */
const LazyLoad = ({ children, fallback, rootMargin = '200px' }) => {
    const [isIntersecting, setIsIntersecting] = useState(false);
    const ref = useRef();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsIntersecting(true);
                    observer.disconnect(); // Stop observing once loaded
                }
            },
            { rootMargin } // Preload before element enters viewport
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [rootMargin]);

    return (
        <div ref={ref}>
            {isIntersecting ? children : fallback}
        </div>
    );
};

export default LazyLoad;
